<template>
  <div class="m-3">
    <div v-if="loading" class="flex items-center justify-center h-full">
      <loading-spinner diameter="64px" />
    </div>

    <alert-badge
      isDanger
      class="my-2"
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <!-- Safes Section -->
    <div class="mb-12">
      <div class="mb-6">
        <div class="flex items-center gap-3">
          <Icon icon="mdi:safe-square" class="text-3xl text-gray-600" />
          <h2 class="text-2xl font-semibold text-gray-800 m-0">{{ t('safes') }}</h2>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <div
          v-for="safe in safes"
          :key="safe.id"
          class="bg-white border border-gray-200 rounded-lg p-6 transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-blue-500 flex flex-col"
        >
          <div class="flex items-start justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-800 min-h-[2.5rem] flex items-center m-0">
              {{ $ctx.locale === 'en' ? safe.enName : safe.arName }}
            </h3>
            <button
              class="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-500 transition-colors"
              :title="t('edit')"
              @click="handleCommand('edit', safe)"
            >
              <Icon icon="mdi:pencil" class="text-lg" />
            </button>
          </div>
          <div class="flex flex-col gap-2 items-center flex-1 justify-center">
            <span
              v-if="$can('create', '/accounting/safes-banks')"
              class="text-2xl font-bold"
              :class="safe.balance < 0 ? 'text-red-600' : 'text-emerald-600'"
              dir="ltr"
            >
              {{ formatMoney(safe.balance, safe.currency || $ctx.currency) }}
            </span>
          </div>
          <div class="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
            <button
              class="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              @click="handleCommand('logs', safe)"
            >
              <Icon icon="mdi:history" class="text-base" />
              {{ t('logs') }}
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-sm text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
              @click="handleCommand('transfer', safe)"
            >
              <Icon icon="mdi:bank-transfer" class="text-base" />
              {{ t('transfer') }}
            </button>
          </div>
        </div>

        <!-- New Safe Button -->
        <div
          class="border-2 border-dashed border-gray-300 bg-gray-50 rounded-lg p-6 cursor-pointer transition-all duration-200 flex items-center justify-center min-h-[140px] hover:border-blue-500 hover:bg-blue-50 hover:-translate-y-0.5 group"
          @click="createNewAccount('safe')"
        >
          <div class="flex flex-col items-center gap-3 text-gray-500 font-medium group-hover:text-blue-500">
            <Icon icon="mdi:plus" class="text-3xl" />
            <span>{{ t('new') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Banks Section -->
    <div class="mb-12">
      <div class="mb-6">
        <div class="flex items-center gap-3">
          <Icon icon="mdi:bank" class="text-3xl text-gray-600" />
          <h2 class="text-2xl font-semibold text-gray-800 m-0">{{ t('banks') }}</h2>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <div
          v-for="bank in banks"
          :key="bank.id"
          class="bg-white border border-gray-200 rounded-lg p-6 transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-blue-500 flex flex-col"
        >
          <div class="flex items-start justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-800 min-h-[2.5rem] flex items-center m-0">
              {{ $ctx.locale === 'en' ? bank.enName : bank.arName }}
            </h3>
            <button
              class="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-500 transition-colors"
              :title="t('edit')"
              @click="handleCommand('edit', bank)"
            >
              <Icon icon="mdi:pencil" class="text-lg" />
            </button>
          </div>
          <div class="flex flex-col gap-2 items-center flex-1 justify-center">
            <span
              v-if="$can('create', '/accounting/safes-banks')"
              class="text-2xl font-bold"
              :class="bank.balance < 0 ? 'text-red-600' : 'text-emerald-600'"
              dir="ltr"
            >
              {{ formatMoney(bank.balance, bank.currency || $ctx.currency) }}
            </span>
          </div>
          <div class="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
            <button
              class="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              @click="handleCommand('logs', bank)"
            >
              <Icon icon="mdi:history" class="text-base" />
              {{ t('logs') }}
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-sm text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
              @click="handleCommand('transfer', bank)"
            >
              <Icon icon="mdi:bank-transfer" class="text-base" />
              {{ t('transfer') }}
            </button>
          </div>
        </div>

        <!-- New Bank Button -->
        <div
          class="border-2 border-dashed border-gray-300 bg-gray-50 rounded-lg p-6 cursor-pointer transition-all duration-200 flex items-center justify-center min-h-[140px] hover:border-blue-500 hover:bg-blue-50 hover:-translate-y-0.5 group"
          @click="createNewAccount('bank')"
        >
          <div class="flex flex-col items-center gap-3 text-gray-500 font-medium group-hover:text-blue-500">
            <Icon icon="mdi:plus" class="text-3xl" />
            <span>{{ t('new') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import { formatMoney } from '@/utils/money'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'

interface SafeBankItem {
  id: string
  enName: string
  arName: string
  balance: number
  currency?: string
}

export default defineComponent({
  name: 'safes-banks',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          safes: 'Safes',
          banks: 'Banks',
          balance: 'Balance',
          new: 'New',
          edit: 'Edit',
          logs: 'Logs',
          transfer: 'New Transfer'
        },
        ar: {
          safes: 'الخزائن',
          banks: 'البنوك',
          balance: 'الرصيد',
          new: 'جديد',
          edit: 'تعديل',
          logs: 'السجل',
          transfer: 'تحويل جديد'
        }
      }
    })

    return { t }
  },

  components: {
    LoadingSpinner,
    AlertBadge,
    Icon
  },

  data() {
    return {
      safes: [] as SafeBankItem[],
      banks: [] as SafeBankItem[],
      loading: false,
      error: {
        title: '',
        body: ''
      }
    }
  },

  async mounted() {
    await this.loadSafesAndBanks()
  },

  methods: {
    formatMoney,

    async loadSafesAndBanks() {
      this.loading = true
      try {
        // Load safes
        try {
          const safesData = await this.$http.get<SafeBankItem[]>('accounting/accounts/safes')
          this.safes = safesData.map(safe => ({
            id: safe.id,
            enName: safe.enName,
            arName: safe.arName,
            balance: safe.balance || 0,
            currency: safe.currency
          }))
        } catch (error) {
          console.warn('Safes endpoint not available, using empty array')
          this.safes = []
        }

        // Load banks
        try {
          const banksData = await this.$http.get<SafeBankItem[]>('accounting/accounts/banks')
          this.banks = banksData.map(bank => ({
            id: bank.id,
            enName: bank.enName,
            arName: bank.arName,
            balance: bank.balance || 0,
            currency: bank.currency
          }))
        } catch (error) {
          console.warn('Banks endpoint not available, using empty array')
          this.banks = []
        }
      } catch (error: any) {
        this.error.title = error.title || 'Error'
        this.error.body = error.body || 'Failed to load safes and banks'
      } finally {
        this.loading = false
      }
    },

    handleCommand(command: string, account: SafeBankItem) {
      switch (command) {
        case 'edit':
          this.$router.push(this.$Route.ACCOUNTING_ACCOUNTS_ACCOUNT.replace(':id', account.id))
          break
        case 'logs':
          this.$router.push(this.$Route.ACCOUNTING_ACCOUNTS_LOG.replace(':id', account.id))
          break
        case 'transfer':
          this.$router.push({
            path: this.$Route.ACCOUNTING_TRANSACTIONS_NEW_TRANSACTION,
            query: {
              fromAccountId: account.id,
              fromAccountName: this.$ctx.locale === 'en' ? account.enName : account.arName
            }
          })
          break
      }
    },

    createNewAccount(type: 'safe' | 'bank') {
      if (type === 'safe') {
        this.$router.push(this.$Route.ACCOUNTING_SAFES_NEW)
      } else {
        this.$router.push(this.$Route.ACCOUNTING_BANKS_NEW)
      }
    }
  }
})
</script>
